import type { DataSession } from "../../../../data/core/session";
import { z } from "zod";
import type { RouterOutputs } from "@utils/trpc";

import {
  songsByAlbumIdsQueryKey,
  songsByPlaylistIdsQueryKey,
} from "@utils/queryConfigs";
import { songSchema } from "@libs/wire";
import { createLocalSongAssets } from "../localAudioPaths";
import type {
  CatalogCollectionType,
  CatalogSong,
  CollectionId,
} from "../../domain/types";

// Legacy alias for historical callers (prefer importing from player domain types)
export type LegacyCatalogSong =
  RouterOutputs["catalog"]["songs"]["getById"][number];

const songsListSchema = z.array(songSchema);

// The artist play queue is the top-20 artist songs by the top-songs scoring
// (product decision, LIS-305): tapping a song on an artist profile queues
// the artist's top 20, not their entire catalog.
const ARTIST_QUEUE_LIMIT = 20;

/**
 * Fetch the songs for a collection (album or playlist) for the playback
 * path.
 *
 * Phase-2.6 cleanup: this used to wrap `fetchHandler` with
 * `offlineFirst: true`, returning locally-downloaded songs synchronously
 * while the remote refetch ran in the background. The original concern
 * was playback latency — `fetchHandler` could short-circuit to the
 * offline path without waiting for the network. The durable SQLite
 * cache retains successful network responses through `withDurableCache`; if
 * the network request rejects, it returns the last cached response. The
 * cached row is also shared with the screen-level
 * `useSongsByAlbumIds` / `useSongsByPlaylistIds` queryKeys, so the data
 * is hot in cache by the time the user presses play in nearly all cases.
 *
 * Architectural shift: server-truth for the song LIST. The cached
 * response says exactly which songs are in the collection; the device
 * then tells us which of those songs has a local audio copy, and we
 * substitute the file path field-level. Songs that exist only locally
 * (orphans, or playlists modified server-side) are NOT included in the
 * playback queue — they live in the "Downloads" view alone.
 */
export function createCollectionsApi(data: DataSession) {
  const trpcVanillaClient = data.api;
  const withDurableCache = data.cache.withDurableCache;
  const { substituteLocalSongAssets } = createLocalSongAssets(data.db);
  async function fetchSongsForCollection(params: {
    collectionType: CatalogCollectionType;
    collectionId: CollectionId;
  }): Promise<CatalogSong[]> {
    const { collectionType, collectionId } = params;

    console.audio.log("fetchSongsForCollection", {
      collectionType,
      collectionId,
    });

    if (collectionType === "album") {
      const queryKey = songsByAlbumIdsQueryKey([collectionId]);
      const songs = await withDurableCache<CatalogSong[]>({
        queryKey,
        fetch: () =>
          trpcVanillaClient.catalog.songs.getByAlbumIds.query([collectionId]),
        schema: songsListSchema,
      });
      return substituteLocalSongAssets(songs);
    }

    if (collectionType === "playlist") {
      const queryKey = songsByPlaylistIdsQueryKey([collectionId]);
      const songs = await withDurableCache<CatalogSong[]>({
        queryKey,
        fetch: () =>
          trpcVanillaClient.catalog.songs.getByPlaylistIds.query([
            collectionId,
          ]),
        schema: songsListSchema,
      });
      return substituteLocalSongAssets(songs);
    }

    // Artist song lists are not currently backed by offline storage.
    console.audio.log("fetchSongsForCollection: artist (online only)", {
      collectionId,
    });
    return await trpcVanillaClient.catalog.songs.getTopSongsByArtistId.query({
      artistId: collectionId,
      numberOfRecommendations: ARTIST_QUEUE_LIMIT,
    });
  }

  async function fetchSimilarSongsForSongIds(params: {
    songIds: string[];
    limit?: number;
    cursorSongId?: string;
  }): Promise<CatalogSong[]> {
    const { songIds, limit = 25, cursorSongId } = params;

    try {
      return await trpcVanillaClient.catalog.songs.getSimilarSongsForSongIds.query(
        {
          songIds: songIds.slice(0, 100),
          numberOfRecommendations: limit,
          cursorSongId,
        },
      );
    } catch (e) {
      console.error("Failed to fetch similar songs", e);
      return [];
    }
  }

  return { fetchSongsForCollection, fetchSimilarSongsForSongIds };
}
